<template>
  <div>
    <div v-if="type != 'boolean'" class="text-heading-6 mb-2">
      <div class="text-primary-1 min-h-4">
        {{ title }}
      </div>
      <div class="text-gray-1 mt-[4px]" v-if="description">
        {{ description }}
      </div>
    </div>
    <div class="relative">
      <input
        v-if="type == 'text' || type == 'email' || type === 'password'"
        ref="textInput"
        :name="name"
        class="bg-gray-3 text-primary-1 rounded-[5px] w-full outline-none px-5 py-2 h-10"
        :class="[
          bgColor,
          { 'border border-red': showError },
          { 'pe-9': type === 'password' },
        ]"
        :value="value"
        :type="checkType"
        :placeholder="placeholder"
        :disabled="disabled"
        autocomplete="false"
        @input="validateInput($event)"
        @blur="setTouched"
      />
      <BaseEyeButton
        v-if="type === 'password'"
        :show="showPasswordValue"
        @click="togglePasswordStatus"
      />
    </div>

    <textarea
      v-if="type == 'textarea'"
      :value="value"
      :name="name"
      class="input-color rounded-[5px] w-full outline-none p-5 placeholder:text-[#787878]"
      :class="{ 'border border-red': showError }"
      :placeholder="placeholder"
      rows="2"
      @input="validateInput($event)"
      @blur="setTouched"
    />
    <BaseSelectOption
      v-if="type == 'select'"
      v-model:option="option"
      :class="{ 'border border-red': showError }"
      class="w-full"
      main-class="!py-3 input-color !rounded-[5px] h-10"
      :placeholder="placeholder"
      :options="options"
    />
    <LazyBaseDatePicker
      v-if="type == 'date'"
      class="input-color rounded-[5px] w-full outline-none px-5 py-2 h-10"
      text-class="!text-primary-1 text-heading-5"
      :class="{ 'border border-red': showError }"
      :initial-date="date"
      :min-date="null"
      :max-date="null"
      :trigger="t('index.search.selectDate')"
      @update-selected="updateSelected"
    />
    <form>
      <InputBoolean
        v-if="type == 'boolean'"
        :value="value"
        :title="title"
        :name="name"
        :default-answer="defaultAnswer"
        @boolean-changed="booleanChanged"
      />
    </form>
    <div
      v-show="showError && type != 'boolean'"
      class="text-[11px] font-normal mt-1 text-red flex gap-1 items-center"
    >
      <icon-warning class="text-red text-[15px] !mb-0" />
      {{ errorMessage }}
    </div>
  </div>
</template>
<script setup lang="ts">
// @ts-nocheck
import { ref, watch } from "vue";
import type { IOptions } from "../../types/general";
import type { PropType } from "vue";
import InputBoolean from "./PaymentFormInputBoolean.vue";
const { t } = useI18n();
const emailPattern = "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$";
import { formattedDate } from "../../composables/general";
const props = defineProps({
  title: {
    type: String,
    default: null,
  },
  description: {
    type: String,
    default: null,
  },
  name: {
    type: String,
    default: "",
  },
  required: {
    type: Boolean,
    default: false,
  },
  value: {
    type: [String, Number, Boolean, Date],
    default: "",
  },
  type: {
    type: String,
    default: "text",
  },
  bgColor: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  options: {
    type: Array as PropType<IOptions[] | []>,
    default: () => [],
  },
  check: {
    type: Boolean,
    default: false,
  },
  defaultAnswer: {
    type: [String, Number, Boolean, Date],
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});
const date = ref(
  props.type == "date" && props.value ? new Date(props.value) : undefined
);
const option = ref<IOptions>({
  id: props.value ?? "",
  title: props.value ?? "",
});
const showError = ref(false);
const errorMessage = ref("");
const textInput = ref<HTMLInputElement | null>(null);
const emit = defineEmits(["update:value", "error-changed"]);
const showPasswordValue = ref<boolean>(false);

const checkType = computed(() => {
  return props.type === "password"
    ? showPasswordValue.value
      ? "text"
      : "password"
    : props.type;
});

function validateInput(event: Event | null = null) {
  let inputValue = "";
  if (event) {
    inputValue = (event.target as HTMLInputElement).value.trim();
  } else if (textInput.value) {
    inputValue = textInput.value.value.trim();
  } else if (props.type === "select") {
    inputValue = option.value?.title || "";
  } else if (props.type === "date") {
    inputValue = date.value ? formattedDate(date.value) : "";
  } else if (props.type === "boolean") {
    inputValue = props.value == "true" ? "true" : "false";
  } else if (props.type === "textarea") {
    inputValue =
      (event?.target as HTMLTextAreaElement)?.value.trim() || props.value || "";
  }
  if (props.required) {
    if (!inputValue) {
      showError.value = true;
      errorMessage.value = t("requiredField");
    } else if (props.type === "email" && !inputValue.match(emailPattern)) {
      showError.value = true;
      errorMessage.value = t("auth.formatIsNotCorrect");
    } else {
      showError.value = false;
      errorMessage.value = "";
    }
  }
  if (props.type !== "select") {
    emit("update:value", inputValue);
  }
}
function setTouched(event: Event) {
  validateInput(event);
}
watch(
  () => props.check,
  (newVal) => {
    if (newVal) {
      // if (newVal && textInput.value) {
      validateInput();
    }
  }
);

watch(
  () => props.value,
  () => {
    if (props.required && props.check) {
      validateInput();
    }
    if (props.type == "date" && props.value) date.value = new Date(props.value);
  }
);
watch(
  option,
  (newVal: IOptions) => {
    emit("update:value", newVal.id);
  },
  { immediate: true }
);
function updateSelected(newDate: Date) {
  date.value = newDate;
  emit("update:value", formattedDate(newDate));
}
function booleanChanged(val: boolean) {
  emit("update:value", val);
}
const togglePasswordStatus = () => {
  showPasswordValue.value = !showPasswordValue.value;
};
watch(showError, (newVal: boolean) => {
  emit("error-changed", newVal);
});
</script>
